/**
 * useAccessLogs - Hook for AccessLogsPage
 *
 * Manages fetching, filtering, pagination, export, and detail modal state
 * for access logs (login/logout/refresh token).
 */
import { useState, useEffect, useMemo } from 'react';
import {
  getAccessLogs,
  getAccessLogDetail,
  exportAccessLogsXlsx,
  AccessLog,
  AccessLogFilters,
  getActionsList,
  ActionOption,
} from '../services/accessLogService';

export interface UseAccessLogsReturn {
  // Filter state
  selectedAcoes: string[];
  setSelectedAcoes: (v: string[]) => void;
  selectedStatus: string[];
  setSelectedStatus: (v: string[]) => void;
  usuarioFilter: string;
  setUsuarioFilter: (v: string) => void;
  ipFilter: string;
  setIpFilter: (v: string) => void;
  acaoOptions: { value: string; label: string }[];
  handleClearFilters: () => void;

  // Data state
  logs: AccessLog[];
  loading: boolean;
  exporting: boolean;
  error: string | null;
  actionsList: ActionOption[];

  // Pagination
  currentPage: number;
  setCurrentPage: (v: number) => void;
  totalCount: number;
  totalPages: number;
  pageSize: number;

  // Detail modal
  selectedLog: AccessLog | null;
  showModal: boolean;
  setShowModal: (v: boolean) => void;

  // Actions
  handleRefresh: () => void;
  handleExport: () => Promise<void>;
  handleViewDetails: (log: AccessLog) => Promise<void>;
  formatDate: (dateStr: string) => string;
}

const PAGE_SIZE = 20;

export const useAccessLogs = (): UseAccessLogsReturn => {
  // Filter state
  const [selectedAcoes, setSelectedAcoes] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [usuarioFilter, setUsuarioFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');

  // Data state
  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<AccessLog | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [actionsList, setActionsList] = useState<ActionOption[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Derived filter object (auto-apply)
  const filters: AccessLogFilters = useMemo(() => {
    const f: AccessLogFilters = {};
    if (selectedAcoes.length > 0) f.acao = selectedAcoes;
    if (selectedStatus.length > 0) f.sucesso = selectedStatus.map(s => s === 'true');
    if (usuarioFilter.trim()) f.usuario = usuarioFilter.trim();
    if (ipFilter.trim()) f.ip = ipFilter.trim();
    return f;
  }, [selectedAcoes, selectedStatus, usuarioFilter, ipFilter]);

  // Options for MultiSelectFilter
  const acaoOptions = actionsList.map(a => ({
    value: a.value,
    label: `${a.label} (${a.count})`,
  }));

  // Load actions list once
  useEffect(() => {
    const loadActions = async () => {
      try {
        const actions = await getActionsList();
        setActionsList(actions);
      } catch (err) {
        console.error('Erro ao carregar actions:', err);
      }
    };
    loadActions();
  }, []);

  // Fetch logs whenever page or filters change
  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAccessLogs({
        page: currentPage,
        page_size: PAGE_SIZE,
        ...filters,
      });
      setLogs(response.results);
      setTotalCount(response.count);
      setTotalPages(response.total_pages);
    } catch (err) {
      setError('Erro ao carregar logs de acesso');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [currentPage, filters]);

  const handleRefresh = () => fetchLogs();

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportAccessLogsXlsx(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `logs_acesso_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Erro ao exportar logs');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const handleViewDetails = async (log: AccessLog) => {
    try {
      const detail = await getAccessLogDetail(log.id);
      setSelectedLog(detail);
      setShowModal(true);
    } catch (err) {
      setSelectedLog(log);
      setShowModal(true);
    }
  };

  const handleClearFilters = () => {
    setSelectedAcoes([]);
    setSelectedStatus([]);
    setUsuarioFilter('');
    setIpFilter('');
    setCurrentPage(1);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return {
    selectedAcoes,
    setSelectedAcoes,
    selectedStatus,
    setSelectedStatus,
    usuarioFilter,
    setUsuarioFilter,
    ipFilter,
    setIpFilter,
    acaoOptions,
    handleClearFilters,
    logs,
    loading,
    exporting,
    error,
    actionsList,
    currentPage,
    setCurrentPage,
    totalCount,
    totalPages,
    pageSize: PAGE_SIZE,
    selectedLog,
    showModal,
    setShowModal,
    handleRefresh,
    handleExport,
    handleViewDetails,
    formatDate,
  };
};
